import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationApi, type Integration } from '@/lib/api';
import { Globe, Trash2, Plus, Edit2, ChevronDown, ChevronUp, Check, X, Plug2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageGuide } from '@/components/PageGuide';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';
import { useNotification } from '@/contexts/NotificationContext';

export const Route = createFileRoute('/_auth/settings/integrations')({
  component: IntegrationsPage,
});

type AuthType = 'NONE' | 'BEARER' | 'API_KEY' | 'BASIC';

const AUTH_LABELS: Record<AuthType, string> = {
  NONE: 'No authentication',
  BEARER: 'Bearer token',
  API_KEY: 'API key header',
  BASIC: 'Basic auth (username/password)',
};

const EMPTY_FORM = {
  name: '',
  type: 'REST_API' as 'REST_API' | 'GRAPHQL',
  baseUrl: '',
  timeout: 5000,
  authType: 'NONE' as AuthType,
  authValue: '',
  apiKeyHeader: 'X-API-Key',
  username: '',
  password: '',
};

type IntegrationFormValues = typeof EMPTY_FORM;

// ── Shared styles ─────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  color: 'var(--color-text)',
  fontSize: 13, width: '100%',
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--color-muted)', textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: 6,
};

const ghostBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 14px', borderRadius: 7,
  border: '1px solid var(--border)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 12, color: 'var(--color-muted)',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

// ── Integration Form ──────────────────────────────────────────────────────

function IntegrationForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: IntegrationFormValues;
  onSave: (v: IntegrationFormValues) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);
  const set = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div style={{ padding: '20px 24px', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 16 }}>
        <Field label="Name">
          <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Customer CRM" />
        </Field>
        <Field label="Type">
          <select style={inputStyle} value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="REST_API">REST API</option>
            <option value="GRAPHQL">GraphQL</option>
          </select>
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Base URL">
            <input style={inputStyle} value={form.baseUrl} onChange={e => set('baseUrl', e.target.value)} placeholder="https://api.example.com" />
          </Field>
        </div>
        <Field label="Timeout (ms)">
          <input style={inputStyle} type="number" value={form.timeout} onChange={e => set('timeout', parseInt(e.target.value))} min={500} max={30000} />
        </Field>
        <Field label="Authentication">
          <select style={inputStyle} value={form.authType} onChange={e => set('authType', e.target.value as AuthType)}>
            {(Object.keys(AUTH_LABELS) as AuthType[]).map(k => (
              <option key={k} value={k}>{AUTH_LABELS[k]}</option>
            ))}
          </select>
        </Field>
        {form.authType === 'BEARER' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Bearer token">
              <input style={inputStyle} type="password" value={form.authValue} onChange={e => set('authValue', e.target.value)} placeholder="eyJ..." />
            </Field>
          </div>
        )}
        {form.authType === 'API_KEY' && (
          <>
            <Field label="Header name">
              <input style={inputStyle} value={form.apiKeyHeader} onChange={e => set('apiKeyHeader', e.target.value)} placeholder="X-API-Key" />
            </Field>
            <Field label="API key value">
              <input style={inputStyle} type="password" value={form.authValue} onChange={e => set('authValue', e.target.value)} placeholder="sk-..." />
            </Field>
          </>
        )}
        {form.authType === 'BASIC' && (
          <>
            <Field label="Username">
              <input style={inputStyle} value={form.username} onChange={e => set('username', e.target.value)} />
            </Field>
            <Field label="Password">
              <input style={inputStyle} type="password" value={form.password} onChange={e => set('password', e.target.value)} />
            </Field>
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={ghostBtnStyle} onClick={onCancel}><X size={12} /> Cancel</button>
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.name || !form.baseUrl}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 18px', borderRadius: 8, border: 'none',
            background: 'var(--color-primary)', color: '#fff',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
            opacity: saving || !form.name || !form.baseUrl ? 0.5 : 1,
          }}
        >
          {saving ? 'Saving…' : <><Check size={13} /> Save</>}
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const qc = useQueryClient();
  const { success, error: notifyError } = useNotification();
  const { data: integrations = [], isLoading } = useQuery({ queryKey: ['integrations'], queryFn: integrationApi.list });
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Integration | null>(null);

  const createMutation = useMutation({
    mutationFn: (v: IntegrationFormValues) => integrationApi.create({
      name: v.name, type: v.type, baseUrl: v.baseUrl,
      timeout: v.timeout, authType: v.authType,
      authValue: v.authType === 'BASIC' ? `${v.username}:${v.password}` : v.authValue,
      apiKeyHeader: v.apiKeyHeader,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      setCreating(false);
      success('Integration created', 'You can now create tools using this integration.');
    },
    onError: (e) => notifyError('Failed to create integration', (e as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, v }: { id: string; v: IntegrationFormValues }) => integrationApi.update(id, {
      name: v.name, type: v.type, baseUrl: v.baseUrl,
      timeout: v.timeout, authType: v.authType,
      authValue: v.authType === 'BASIC' ? `${v.username}:${v.password}` : v.authValue,
      apiKeyHeader: v.apiKeyHeader,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      setEditingId(null);
      success('Integration updated');
    },
    onError: (e) => notifyError('Failed to update integration', (e as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => integrationApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      setDeleteTarget(null);
      success('Integration deleted');
    },
    onError: (e) => notifyError('Failed to delete integration', (e as Error).message),
  });

  const toggleExpand = (id: string) => {
    setExpanded(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  return (
    <div className="page">
      <PageGuide pageKey="integrations" />

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Integrations</h1>
            <p style={{ color: 'var(--color-muted)', fontSize: 13, marginTop: 6 }}>
              Connect external services. Tools are built on top of integrations.
            </p>
          </div>
          <button
            onClick={() => setCreating(true)}
            disabled={creating}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: 'var(--color-primary)', color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
              transition: 'transform 0.15s, box-shadow 0.15s',
              opacity: creating ? 0.5 : 1,
            }}
            onMouseEnter={e => { if (!creating) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.4)'; } }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.3)'; }}
          >
            <Plus size={15} /> New Integration
          </button>
        </div>

        {/* Stats row */}
        {integrations.length > 0 && (
          <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Total', value: integrations.length, color: '#6366f1' },
              { label: 'REST API', value: (integrations as Integration[]).filter(i => i.type === 'REST_API').length, color: '#06b6d4' },
              { label: 'GraphQL', value: (integrations as Integration[]).filter(i => i.type === 'GRAPHQL').length, color: '#8b5cf6' },
              { label: 'Active', value: (integrations as Integration[]).filter(i => i.active).length, color: '#10b981' },
              { label: 'Total Tools', value: (integrations as Integration[]).reduce((s, i) => s + (i._count?.toolDefinitions ?? 0), 0), color: '#f59e0b' },
            ].map(s => (
              <div key={s.label} style={{
                padding: '10px 16px', borderRadius: 10,
                background: `${s.color}10`, border: `1px solid ${s.color}25`,
                minWidth: 80,
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 1 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Create form ── */}
      {creating && (
        <div style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--color-primary)33',
          borderRadius: 14,
          marginBottom: 20,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Plug2 size={16} style={{ color: 'var(--color-primary)' }} /> New Integration
            </h3>
          </div>
          <IntegrationForm
            initial={EMPTY_FORM}
            onSave={v => createMutation.mutate(v)}
            onCancel={() => setCreating(false)}
            saving={createMutation.isPending}
          />
        </div>
      )}

      {/* ── Content ── */}
      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ height: 80, borderRadius: 14, background: 'var(--surface-2)', opacity: 0.4, animation: 'pulse 1.5s ease infinite' }} />
          ))}
        </div>
      ) : integrations.length === 0 && !creating ? (
        <SettingsEmptyState
          icons={[
            { icon: <Globe size={18} />, color: '#06b6d4' },
            { icon: <Plug2 size={18} />, color: '#8b5cf6' },
          ]}
          title="Connect your first integration"
          description="Integrations are the bridges between your agents and external services — CRMs, APIs, databases. Tools are then built on top of them."
          buttonLabel="Add Integration"
          onAction={() => setCreating(true)}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(integrations as Integration[]).map(intg => (
            <div
              key={intg.id}
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 14,
                overflow: 'hidden',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f155'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: intg.type === 'REST_API' ? '#06b6d4' : '#8b5cf6',
                      background: intg.type === 'REST_API' ? '#06b6d415' : '#8b5cf615',
                      border: `1px solid ${intg.type === 'REST_API' ? '#06b6d430' : '#8b5cf630'}`,
                      borderRadius: 5, padding: '2px 7px',
                    }}>
                      {intg.type}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{intg.name}</span>
                    {!intg.active && (
                      <span style={{ fontSize: 10, background: '#6b728015', color: '#6b7280', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>Inactive</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--color-muted)', fontFamily: 'monospace' }}>{intg.config?.baseUrl}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: '#f59e0b',
                      background: '#f59e0b12', border: '1px solid #f59e0b25',
                      borderRadius: 4, padding: '1px 6px',
                    }}>
                      {intg._count?.toolDefinitions ?? 0} tools
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    style={ghostBtnStyle}
                    onClick={() => toggleExpand(intg.id)}
                    title="Expand details"
                  >
                    {expanded.has(intg.id) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  <button style={ghostBtnStyle} onClick={() => setEditingId(intg.id)} title="Edit">
                    <Edit2 size={13} />
                  </button>
                  <button
                    style={{ ...ghostBtnStyle, borderColor: '#ef444433', color: '#ef4444' }}
                    onClick={() => setDeleteTarget(intg)}
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {editingId === intg.id && (
                <IntegrationForm
                  initial={{
                    name: intg.name,
                    type: intg.type,
                    baseUrl: intg.config?.baseUrl ?? '',
                    timeout: intg.config?.timeout ?? 5000,
                    authType: (intg.config?.authType ?? 'NONE') as AuthType,
                    authValue: intg.config?.authValue ?? '',
                    apiKeyHeader: intg.config?.apiKeyHeader ?? 'X-API-Key',
                    username: '',
                    password: '',
                  }}
                  onSave={v => updateMutation.mutate({ id: intg.id, v })}
                  onCancel={() => setEditingId(null)}
                  saving={updateMutation.isPending}
                />
              )}

              {expanded.has(intg.id) && editingId !== intg.id && (
                <div style={{ padding: '12px 20px 16px', borderTop: '1px solid var(--border)' }}>
                  {[
                    { label: 'Auth type', value: intg.config?.authType ?? 'NONE' },
                    { label: 'Timeout', value: `${intg.config?.timeout ?? 5000}ms` },
                    { label: 'Created', value: new Date(intg.createdAt).toLocaleDateString() },
                  ].map(row => (
                    <div key={row.label} style={{ display: 'flex', gap: 12, fontSize: 12, padding: '3px 0' }}>
                      <span style={{ color: 'var(--color-muted)', minWidth: 80 }}>{row.label}</span>
                      <span style={{ color: 'var(--color-text)' }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title={`Delete "${deleteTarget?.name}"?`}
        description={`This will remove the integration and may break any tools connected to it (${deleteTarget?._count?.toolDefinitions ?? 0} tools).`}
        confirmLabel="Delete Integration"
        variant="danger"
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

